import { useState } from "react";
import { motion } from "framer-motion";

interface CountryData {
  id: string;
  name: string;
  projects: number;
  x: number;
  y: number;
  labelX?: number;
  labelY?: number;
  labelAnchor?: "start" | "end";
}

const countries: CountryData[] = [
  { id: "SN", name: "Sénégal", projects: 13, x: 24.5, y: 39, labelX: -2, labelY: -3, labelAnchor: "end" },
  { id: "DJ", name: "Djibouti", projects: 3, x: 62.5, y: 42.5, labelX: 4, labelY: 0, labelAnchor: "start" },
  { id: "GA", name: "Gabon", projects: 1, x: 39.5, y: 56, labelX: -2, labelY: 0, labelAnchor: "end" },
  { id: "CG", name: "Congo B.", projects: 2, x: 43.5, y: 58.5, labelX: 4, labelY: 0, labelAnchor: "start" },
  { id: "CD", name: "RD Congo", projects: 1, x: 48.5, y: 57, labelX: 4, labelY: -2, labelAnchor: "start" },
  { id: "KM", name: "Comores", projects: 2, x: 62.5, y: 67.5, labelX: 4, labelY: 0, labelAnchor: "start" },
  { id: "BJ", name: "Bénin", projects: 1, x: 34.5, y: 47.5, labelX: 3, labelY: -1, labelAnchor: "start" },
];

const totalProjects = countries.reduce((sum, c) => sum + c.projects, 0);

const AfricaMap = () => {
  const [hoveredCountry, setHoveredCountry] = useState<CountryData | null>(null);

  const getSize = (projects: number) => {
    if (projects >= 10) return 26;
    if (projects >= 3) return 16;
    return 10;
  };

  const getOpacity = (projects: number) => {
    if (projects >= 10) return 1;
    if (projects >= 3) return 0.8;
    return 0.55;
  };

  return (
    <div className="relative">
      {/* Total counter */}
      <div className="text-center mb-4">
        <span className="text-4xl font-bold text-foreground font-display">{totalProjects}</span>
        <p className="text-sm text-muted-foreground">projets en Afrique</p>
      </div>

      {/* Map */}
      <div className="relative mx-auto" style={{ maxWidth: 420 }}>
        <img
          src="/Afrique.svg"
          alt="Carte de l'Afrique"
          className="w-full h-auto opacity-30"
          draggable={false}
        />

        {/* Country markers + labels */}
        {countries.map((country) => {
          const size = getSize(country.projects);
          const isHovered = hoveredCountry?.id === country.id;
          const displaySize = isHovered ? size + 6 : size;

          return (
            <div key={country.id}>
              {/* Marker */}
              <div
                className="absolute cursor-pointer z-[2]"
                style={{
                  left: `${country.x}%`,
                  top: `${country.y}%`,
                  transform: "translate(-50%, -50%)",
                }}
                onMouseEnter={() => setHoveredCountry(country)}
                onMouseLeave={() => setHoveredCountry(null)}
              >
                {/* Glow */}
                <div
                  className="absolute rounded-full transition-all duration-300"
                  style={{
                    width: displaySize + 8,
                    height: displaySize + 8,
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: `hsl(var(--brand-red) / ${isHovered ? 0.25 : 0.1})`,
                  }}
                />
                {/* Pulse on hover */}
                {isHovered && (
                  <div
                    className="absolute rounded-full animate-ping"
                    style={{
                      width: displaySize + 12,
                      height: displaySize + 12,
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      border: "2px solid hsl(var(--brand-red) / 0.4)",
                    }}
                  />
                )}
                {/* Main dot */}
                <div
                  className="relative rounded-full border-2 border-white flex items-center justify-center transition-all duration-300 shadow-md"
                  style={{
                    width: displaySize,
                    height: displaySize,
                    backgroundColor: `hsl(var(--brand-red) / ${getOpacity(country.projects)})`,
                  }}
                >
                  {(country.projects >= 5 || isHovered) && (
                    <span
                      className="text-white font-bold select-none pointer-events-none"
                      style={{ fontSize: isHovered ? 10 : 8 }}
                    >
                      {country.projects}
                    </span>
                  )}
                </div>
              </div>

              {/* Country label */}
              <div
                className="absolute z-[1] pointer-events-none"
                style={{
                  left: `${country.x + (country.labelX ?? 0)}%`,
                  top: `${country.y + (country.labelY ?? 0)}%`,
                  transform: "translate(0, -50%)",
                  textAlign: country.labelAnchor === "end" ? "right" : "left",
                }}
              >
                <span
                  className="text-[9px] font-semibold leading-tight whitespace-nowrap"
                  style={{
                    color: isHovered ? "hsl(var(--brand-red))" : "hsl(var(--foreground) / 0.6)",
                    transition: "color 0.2s",
                  }}
                >
                  {country.name}
                </span>
              </div>
            </div>
          );
        })}

        {/* Tooltip */}
        {hoveredCountry && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-4 right-4 bg-card border border-border rounded-xl p-3 shadow-lg z-10"
          >
            <p className="font-semibold text-foreground text-sm">{hoveredCountry.name}</p>
            <p className="text-xs text-muted-foreground">
              <span className="text-[hsl(var(--brand-red))] font-bold">{hoveredCountry.projects}</span>{" "}
              projet{hoveredCountry.projects > 1 ? "s" : ""}
            </p>
          </motion.div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-red)/0.55)]" />
          <span>1-2</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-[hsl(var(--brand-red)/0.8)]" />
          <span>3-4</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded-full bg-[hsl(var(--brand-red))]" />
          <span>10+</span>
        </div>
      </div>
    </div>
  );
};

export default AfricaMap;
